import type { FocusableOption } from '@angular/cdk/a11y';
import { FocusMonitor } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ENTER, SPACE } from '@angular/cdk/keycodes';
import { Platform } from '@angular/cdk/platform';
import { ViewportRuler } from '@angular/cdk/scrolling';
import type { AfterContentChecked, AfterContentInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Attribute, HostListener } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  NgZone,
  Optional,
  QueryList,
  ViewChild,
  ViewEncapsulation,
  HostBinding,
  Directive,
} from '@angular/core';
import type { RippleConfig, RippleGlobalOptions, RippleTarget } from '@angular/material/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleRenderer } from '@angular/material/core';
import type { CanDisableRipple, HasTabIndex } from '../../core/common';
import { mixinDisabled, mixinDisableRipple, mixinTabIndex, ThemePalette } from '../../core/common';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import type { CanDisable } from '@shared/material/core/common/disabled';
import { startWith, takeUntil } from 'rxjs/operators';
import { MatInkBarDirective } from '../ink-bar.directive';
import { MatPaginatedTabHeader } from '../paginated-tab-header';

// Increasing integer for generating unique ids for tab nav components.
let nextUniqueId = 0;

/**
 * Navigation component matching the styles of the tab group header.
 * Provides anchored navigation with animated ink bar.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-mat-tab-nav-bar]',
  exportAs: 'matTabNavBar, matTabNav',
  templateUrl: 'tab-nav-bar.component.html',
  styleUrls: ['tab-nav-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Default,
})
export class MatTabNavComponent
  extends MatPaginatedTabHeader
  implements AfterContentChecked, AfterContentInit, OnDestroy
{
  /** Theme color of the nav bar. */
  @Input() color: ThemePalette = 'primary';

  /** Query list of all tab links of the tab navigation. */
  @ContentChildren(forwardRef(() => MatTabLinkDirective), { descendants: true })
  override _items: QueryList<MatTabLinkDirective>;

  @ViewChild(MatInkBarDirective, { static: true }) _inkBar: MatInkBarDirective;
  @ViewChild('tabListContainer', { static: true }) _tabListContainer: ElementRef;
  @ViewChild('tabList', { static: true }) _tabList: ElementRef;
  @ViewChild('tabListInner', { static: true }) _tabListInner: ElementRef;
  @ViewChild('nextPaginator') _nextPaginator: ElementRef<HTMLElement>;
  @ViewChild('previousPaginator') _previousPaginator: ElementRef<HTMLElement>;

  @HostBinding('class')
  readonly hostClass = 'mat-tab-nav-bar mat-tab-header';

  private _backgroundColor: ThemePalette;
  private _disableRipple: boolean = false;

  /** Background color of the tab nav. */
  @Input()
  get backgroundColor(): ThemePalette {
    return this._backgroundColor;
  }

  set backgroundColor(value: ThemePalette) {
    const classList = this._elementRef.nativeElement.classList;
    classList.remove('mat-tabs-with-background', `mat-background-${this.backgroundColor}`);

    if (value) {
      classList.add('mat-tabs-with-background', `mat-background-${value}`);
    }

    this._backgroundColor = value;
  }

  /** Whether the ripple effect is disabled or not. */
  @Input()
  get disableRipple(): boolean {
    return this._disableRipple;
  }

  set disableRipple(value: BooleanInput) {
    this._disableRipple = coerceBooleanProperty(value);
  }

  @HostBinding('class.mat-tab-header-pagination-controls-enabled')
  get showPaginationControls(): boolean {
    return this._showPaginationControls;
  }

  @HostBinding('class.mat-tab-header-rtl')
  get isRtl(): boolean {
    return this._getLayoutDirection() === 'rtl';
  }

  @HostBinding('class.mat-primary')
  get isPrimary(): boolean {
    return this.color !== 'warn' && this.color !== 'accent';
  }

  @HostBinding('class.mat-accent')
  get isAccent(): boolean {
    return this.color === 'accent';
  }

  @HostBinding('class.mat-warn')
  get isWarn(): boolean {
    return this.color === 'warn';
  }

  constructor(
    elementRef: ElementRef,
    @Optional() dir: Directionality,
    ngZone: NgZone,
    changeDetectorRef: ChangeDetectorRef,
    viewportRuler: ViewportRuler,
    platform: Platform,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) animationMode?: string,
  ) {
    super(elementRef, changeDetectorRef, viewportRuler, dir, ngZone, platform, animationMode);
  }

  override ngAfterContentInit() {
    // We need this to run before the `changes` subscription in parent to ensure that the
    // selectedIndex is up-to-date by the time the super class starts looking for it.
    this._items.changes.pipe(startWith(null), takeUntil(this._destroyed)).subscribe(() => {
      this.updateActiveLink();
    });

    super.ngAfterContentInit();
  }

  /** Notifies the component that the active link has been changed. */
  updateActiveLink() {
    if (!this._items) {
      return;
    }

    const items = this._items.toArray();

    for (let i = 0; i < items.length; i++) {
      if (items[i].active) {
        this.selectedIndex = i;
        this._changeDetectorRef.markForCheck();

        return;
      }
    }

    // The ink bar should hide itself if no items are active.
    this.selectedIndex = -1;
    this._inkBar.hide();
  }

  protected _itemSelected() {
    // noop
  }
}

// Boilerplate for applying mixins to MatTabLink.
const _MatTabLinkMixinBase = mixinTabIndex(mixinDisableRipple(mixinDisabled(class {})));

/**
 * Link inside of a `mat-tab-nav-bar`.
 */
@Directive({
  selector: '[app-mat-tab-link], [appMatTabLink]',
  exportAs: 'matTabLink',
})
export class MatTabLinkDirective
  extends _MatTabLinkMixinBase
  implements AfterViewInit, OnDestroy, CanDisable, CanDisableRipple, HasTabIndex, RippleTarget, FocusableOption
{
  @HostBinding('class.mat-tab-disabled')
  @HostBinding('disabled')
  @Input()
  override disabled = super.disabled;

  @Input() override disableRipple = super.disableRipple;
  @Input() override tabIndex: number = super.tabIndex;

  /** Unique id for the tab. */
  @HostBinding('attr.id')
  @Input()
  id = `mat-tab-link-${nextUniqueId++}`;

  @HostBinding('class')
  readonly hostClass = 'mat-tab-link mat-focus-indicator';

  /**
   * Ripple configuration for ripples that are launched on pointer down. The ripple config
   * is set to the global ripple options since we don't have any configurable options for
   * the tab link ripples.
   */
  rippleConfig: RippleConfig & RippleGlobalOptions;

  /** Whether the tab link is active or not. */
  protected _isActive: boolean = false;

  /** Reference to the RippleRenderer for the tab-link. */
  private _tabLinkRipple: RippleRenderer;

  /** Whether the link is active. */
  @HostBinding('class.mat-tab-label-active')
  @Input()
  get active(): boolean {
    return this._isActive;
  }

  set active(value: BooleanInput) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this._isActive) {
      this._isActive = newValue;
      this._tabNavBar.updateActiveLink();
    }
  }

  /**
   * Whether ripples are disabled on interaction.
   */
  get rippleDisabled(): boolean {
    return this.disabled || this.disableRipple || this._tabNavBar.disableRipple || !!this.rippleConfig.disabled;
  }

  constructor(
    private _tabNavBar: MatTabNavComponent,
    public elementRef: ElementRef,
    ngZone: NgZone,
    platform: Platform,
    @Optional() @Inject(MAT_RIPPLE_GLOBAL_OPTIONS) globalRippleOptions: RippleGlobalOptions | null,
    @Attribute('tabindex') tabIndex: string,
    private _focusMonitor: FocusMonitor,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) animationMode?: string,
  ) {
    super();

    this.rippleConfig = globalRippleOptions || {};
    this.tabIndex = parseInt(tabIndex) || 0;

    if (animationMode === 'NoopAnimations') {
      this.rippleConfig.animation = { enterDuration: 0, exitDuration: 0 };
    }

    this._tabLinkRipple = new RippleRenderer(this, ngZone, elementRef, platform);
    this._tabLinkRipple.setupTriggerEvents(elementRef.nativeElement);
  }

  @HostBinding('attr.aria-current')
  _getAriaCurrent(): string | null {
    return this.active ? 'page' : null;
  }

  @HostBinding('attr.tabIndex')
  _getTabIndex(): number {
    return this.tabIndex;
  }

  @HostListener('focus')
  _handleFocus() {
    // Since we allow navigation through tabbing in the nav bar, we
    // have to update the focused index whenever the link receives focus.
    this._tabNavBar.focusIndex = this._tabNavBar._items.toArray().indexOf(this);
  }

  @HostListener('keydown', ['$event'])
  _handleKeydown(event: KeyboardEvent) {
    if ((event.keyCode === SPACE || event.keyCode === ENTER) && this.disabled) {
      event.preventDefault();
    }
  }

  /** Focuses the tab link. */
  focus() {
    this.elementRef.nativeElement.focus();
  }

  ngAfterViewInit() {
    this._focusMonitor.monitor(this.elementRef);
  }

  ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this.elementRef);
    this._tabLinkRipple._removeTriggerEvents();
  }
}
